/**
 * Standalone cache utilities
 * Can be used anywhere in the app, not just in React components
 * Uses the same caching mechanism as useCache hook
 */

type CacheEntry<T = unknown> = {
  data: T
  timestamp: number
}

type CacheInstance = {
  prefix: string
  expiryMs: number
  memory: Map<string, CacheEntry>
}

const memoryCaches = new Map<string, CacheInstance>()

/**
 * Create or get a cache instance
 */
const getCacheInstance = (prefix: string, expiryMs: number): CacheInstance => {
  const cacheKey = `${prefix}_${expiryMs}`
  if (!memoryCaches.has(cacheKey)) {
    memoryCaches.set(cacheKey, {
      prefix,
      expiryMs,
      memory: new Map(),
    })
  }
  return memoryCaches.get(cacheKey)!
}

/**
 * Get cache key for a given ID
 */
const getCacheKey = (
  prefix: string,
  id: string | number | null | undefined,
): string => {
  const idStr = id === null || id === undefined ? 'null' : String(id)
  return `${prefix}${idStr}`
}

/**
 * Generic cache getter
 */
export const getCached = <T = unknown>(
  prefix: string,
  id: string | number | null | undefined,
  expiryMs: number = 5 * 60 * 1000,
): T | null => {
  const cache = getCacheInstance(prefix, expiryMs)
  const cacheKey = getCacheKey(prefix, id)
  const now = Date.now()

  const memoryEntry = cache.memory.get(cacheKey)
  if (memoryEntry) {
    const { data, timestamp } = memoryEntry
    if (now - timestamp < expiryMs) {
      return data as T
    }
    // Expired, remove from memory
    cache.memory.delete(cacheKey)
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(cacheKey)
    if (stored) {
      const parsed: CacheEntry<T> = JSON.parse(stored)
      const { data, timestamp } = parsed
      if (now - timestamp < expiryMs) {
        // Update memory cache
        cache.memory.set(cacheKey, { data, timestamp })
        return data
      }
      // Expired, remove from localStorage
      localStorage.removeItem(cacheKey)
    }
  } catch (error) {
    console.error(`Error reading cache for ${cacheKey}:`, error)
    try {
      localStorage.removeItem(cacheKey)
    } catch {
      // Ignore
    }
  }

  return null
}

/**
 * Generic cache setter
 */
export const setCached = <T = unknown>(
  prefix: string,
  id: string | number | null | undefined,
  data: T,
  expiryMs: number = 5 * 60 * 1000,
): void => {
  const cache = getCacheInstance(prefix, expiryMs)
  const cacheKey = getCacheKey(prefix, id)
  const timestamp = Date.now()
  const entry: CacheEntry<T> = { data, timestamp }

  // Update memory cache
  cache.memory.set(cacheKey, entry)

  // Update localStorage
  try {
    localStorage.setItem(cacheKey, JSON.stringify(entry))
  } catch (error) {
    console.error(`Error writing cache for ${cacheKey}:`, error)
    try {
      clearExpired(prefix, expiryMs)
      localStorage.setItem(cacheKey, JSON.stringify(entry))
    } catch (e) {
      console.error(`Failed to cache data for ${cacheKey}:`, e)
    }
  }
}

/**
 * Generic cache invalidator
 */
export const invalidateCache = (
  prefix: string,
  id: string | number | null | undefined,
): void => {
  const cacheKey = getCacheKey(prefix, id)

  for (const cache of memoryCaches.values()) {
    if (cache.prefix === prefix) {
      cache.memory.delete(cacheKey)
    }
  }

  try {
    localStorage.removeItem(cacheKey)
  } catch (error) {
    console.error(`Error invalidating cache for ${cacheKey}:`, error)
  }
}

/**
 * Invalidate all cache entries with a prefix
 */
export const invalidateAllCache = (prefix: string): void => {
  // Clear memory cache
  for (const cache of memoryCaches.values()) {
    if (cache.prefix === prefix) {
      cache.memory.clear()
    }
  }

  // Clear localStorage cache
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.error(`Error clearing cache for prefix ${prefix}:`, error)
  }
}

/**
 * Clear expired cache entries
 */
export const clearExpired = (
  prefix: string,
  expiryMs: number = 5 * 60 * 1000,
): void => {
  const now = Date.now()

  // Clear expired memory cache entries
  for (const cache of memoryCaches.values()) {
    if (cache.prefix === prefix) {
      for (const [key, entry] of cache.memory.entries()) {
        if (now - entry.timestamp >= expiryMs) {
          cache.memory.delete(key)
        }
      }
    }
  }

  // Clear expired localStorage entries
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const parsed: CacheEntry = JSON.parse(stored)
            const { timestamp } = parsed
            if (now - timestamp >= expiryMs) {
              keysToRemove.push(key)
            }
          }
        } catch {
          // Invalid entry, remove it
          keysToRemove.push(key)
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.error(`Error clearing expired cache for prefix ${prefix}:`, error)
  }
}
